import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import axios from 'axios';
import io from 'socket.io-client';
import CODTournamentSystem from './components/CODTournamentSystem';
import LoadingSpinner from './components/LoadingSpinner';
import PublicProfile from './components/PublicProfile/PublicProfile';
import PublicLeaderboard from './components/PublicProfile/PublicLeaderboard';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

// Import RTL utilities
import { forceRTLInitialization, RTLDebugger } from './hooks/useRTLLanguage';
import { translations, getTranslation, isRTL } from './translations/translations';

// Enhanced CSS imports for COD Mobile style
import './App.css';
import './components/CODTournamentSystem.css';
import './rtl-support.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

const paypalOptions = {
  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture"
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [serverHealth, setServerHealth] = useState('unknown');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  // Enhanced online users management with better state handling
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineUsersLastUpdate, setOnlineUsersLastUpdate] = useState(0);
  const [socketReady, setSocketReady] = useState(false);

  // Enhanced language state management with RTL support
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    return savedLanguage || 'en';
  });

  // Translation function using the translation system
  const t = (key, fallback = null) => {
    return getTranslation(key, currentLanguage, fallback) || fallback || key;
  };

  // CRITICAL: Force RTL initialization on app startup with COD Mobile enhancements
  useEffect(() => {
    console.log('🚀 COD Mobile App mounted - forcing RTL initialization');
    forceRTLInitialization();
    
    // Apply current language state with enhanced COD styling
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    // Add COD Mobile theme class
    htmlElement.classList.add('cod-mobile-theme');
    
    console.log('✅ COD Mobile App RTL initialization completed');
    
    setupAxiosInterceptors(); 
    initializeApp(); 
  }, []);

  // Apply RTL when language changes with enhanced notifications
  useEffect(() => {
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    localStorage.setItem('preferred-language', currentLanguage);
    
    console.log('🌐 COD Mobile App language changed:', {
      language: currentLanguage,
      isRtl,
      direction: htmlElement.getAttribute('dir'),
      hasRtlClass: htmlElement.classList.contains('rtl-layout')
    });
  }, [currentLanguage]);

  useEffect(() => { 
    if (user && !socket) {
      initializeSocket(localStorage.getItem('token')); 
    }
    // Note: Don't disconnect in cleanup - let logout handle it
    // The socket should persist while user is logged in
  }, [user, socket]);

  // Enhanced server health monitoring with COD-style notifications
  useEffect(() => { 
    const interval = setInterval(() => {
      fetch(`${API_BASE_URL}/api/health`, { timeout: 5000 })
        .then(res => res.json())
        .then(health => {
          const isHealthy = health.status === 'healthy';
          if (!isHealthy && serverHealth !== 'unhealthy') {
            setServerHealth('unhealthy');
            toast.error('🔴 Server temporarily unavailable', { 
              id: 'health-503', 
              duration: 10000,
              style: {
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
                color: '#ef4444',
                border: '1px solid #ef4444',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: '700'
              }
            });
          } else if (isHealthy && serverHealth === 'unhealthy') {
            setServerHealth('healthy');
            toast.success('🟢 Server connection restored', { 
              id: 'health-restored',
              style: {
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
                color: '#10b981',
                border: '1px solid #10b981',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: '700'
              }
            });
          }
        })
        .catch(() => setServerHealth('unknown'));
    }, 30000);
    return () => clearInterval(interval);
  }, [serverHealth]);

  useEffect(() => {
    const interval = setInterval(refreshTokenIfNeeded, 60000);
    return () => clearInterval(interval);
  }, []);

  // Enhanced axios interceptors with COD-style error handling
  const setupAxiosInterceptors = () => {
    axios.interceptors.request.use(config => {
      const token = localStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    axios.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 503) {
          if (!toast.isActive('db-unavailable')) {
            toast.error('⚠️ Database temporarily unavailable. Retrying...', { 
              id: 'db-unavailable', 
              duration: 10000,
              style: {
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(245, 158, 11, 0.2))',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: '700'
              }
            });
          }
          const retryDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          setReconnectAttempts(prev => prev + 1);
          
          return new Promise((resolve, reject) => {
            setTimeout(async () => {
              try {
                const retryResponse = await axios.request({
                  ...error.config,
                  headers: { ...error.config.headers, 'X-Retry-Attempt': reconnectAttempts + 1 }
                });
                setReconnectAttempts(0);
                resolve(retryResponse);
              } catch (retryError) {
                if (reconnectAttempts < 3) reject(retryError);
                else {
                  setReconnectAttempts(0);
                  toast.error('🚨 Unable to connect to server. Please refresh the page.', { 
                    id: 'max-retries-reached',
                    style: {
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(220, 38, 38, 0.2))',
                      color: '#dc2626',
                      border: '1px solid #dc2626',
                      fontFamily: "'Orbitron', monospace",
                      fontWeight: '700'
                    }
                  });
                  reject(retryError);
                }
              }
            }, retryDelay);
          });
        }
        
        if (error.response?.status === 401 && !error.response?.data?.retry) logout();
        if (error.response?.status === 403 && error.response?.data?.banned) logout();
        return Promise.reject(error);
      }
    );
  };

  const initializeApp = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        if (isTokenExpired(token)) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.post('/api/auth/verify');
        if (response.data.valid) {
          setUser(response.data.user);
        } else {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
    } catch (error) {
      if (error.response?.status !== 503) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  // Enhanced socket initialization with COD-style notifications
  const initializeSocket = (token) => {
    const newSocket = io(API_BASE_URL, {
      auth: { token },
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 10000,
      forceNew: true,
      transports: ['websocket', 'polling']
    });

    const socketEvents = {
      connect: () => {
        console.log('🔗 Socket connected');
        setConnectionStatus('connected');
        setSocketReady(true);
        setReconnectAttempts(0);
        toast.dismiss(['socket-error', 'socket-auth-error']);
        toast.success('🟢 ' + getTranslation('connected', localStorage.getItem('preferred-language') || 'en', 'Connected to server!'), { 
          id: 'socket-connected',
          duration: 2000,
          style: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
            color: '#10b981',
            border: '1px solid #10b981',
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: '700'
          }
        });
        
        // Join user-specific room
        newSocket.emit('join', { 
          userId: user._id || user.id, 
          username: user.username,
          inGameName: user.inGameName,
          role: user.role 
        });
      },

      disconnect: (reason) => {
        console.log('🔌 Socket disconnected:', reason);
        setConnectionStatus('disconnected');
        setSocketReady(false);
        setOnlineUsers([]); // Clear online users on disconnect
        if (reason !== 'io client disconnect') toast.dismiss('socket-connected');
        if (reason === "io server disconnect" && !newSocket._connectionLimited) newSocket.connect();
      },

      connectionLimitExceeded: (data) => {
        console.log("⚠️ Connection limit exceeded:", data);
        newSocket._connectionLimited = true;
        toast.error("Too many active sessions. Please close other tabs.", {
          id: "connection-limit",
          duration: 10000
        });
        newSocket.disconnect();
      },


      connect_error: (error) => {
        console.error('❌ Socket connection error:', error);
        setConnectionStatus('error');
        setSocketReady(false);
        setOnlineUsers([]); // Clear online users on error
        
        if (error.message === 'Service temporarily unavailable') {
          if (!toast.isActive('socket-unavailable')) {
            toast.error('⚠️ Server temporarily unavailable. Retrying...', { 
              id: 'socket-unavailable', 
              duration: 8000,
              style: {
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(245, 158, 11, 0.2))',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: '700'
              }
            });
          }
          return;
        }
        if (error.message === 'Authentication error') {
          toast.error('🔐 Authentication failed. Please login again.', { 
            id: 'socket-auth-error',
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
              color: '#ef4444',
              border: '1px solid #ef4444',
              fontFamily: "'Orbitron', monospace",
              fontWeight: '700'
            }
          });
          logout();
          return;
        }
        if (error.message === 'Account banned') {
          toast.error('🚫 Your account has been banned', { 
            id: 'account-banned',
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(127, 29, 29, 0.3))',
              color: '#dc2626',
              border: '1px solid #dc2626',
              fontFamily: "'Orbitron', monospace",
              fontWeight: '700'
            }
          });
          logout();
          return;
        }
        if (!toast.isActive('socket-error')) {
          toast.error('🔄 Connection error. Trying to reconnect...', { 
            id: 'socket-error', 
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
              color: '#ef4444',
              border: '1px solid #ef4444',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: '700'
            }
          });
        }
      },

      reconnect: (attemptNumber) => {
        setConnectionStatus('connected');
        setSocketReady(true);
        setReconnectAttempts(0);
        toast.dismiss(['socket-error', 'socket-unavailable']);
        toast.success('🔄 Reconnected to server!', { 
          id: 'socket-reconnected',
          style: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
            color: '#10b981',
            border: '1px solid #10b981',
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: '700'
          }
        });
      },

      reconnect_attempt: (attemptNumber) => {
        setConnectionStatus('connecting');
        setReconnectAttempts(attemptNumber);
        if (attemptNumber === 1) {
          toast.loading('🔄 Reconnecting to server...', { 
            id: 'socket-reconnecting',
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(59, 130, 246, 0.2))',
              color: '#3b82f6',
              border: '1px solid #3b82f6',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: '700'
            }
          });
        } else if (attemptNumber === 5) {
          toast.loading('⏳ Still trying to reconnect...', { 
            id: 'socket-reconnecting',
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(245, 158, 11, 0.2))',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: '700'
            }
          });
        }
      },

      reconnect_failed: () => {
        setConnectionStatus('failed');
        setSocketReady(false);
        setOnlineUsers([]); // Clear online users on failed reconnection
        toast.dismiss('socket-reconnecting');
        toast.error('🚨 Failed to connect to server. Please refresh the page.', { 
          id: 'socket-failed', 
          duration: 10000,
          style: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(127, 29, 29, 0.3))',
            color: '#dc2626',
            border: '1px solid #dc2626',
            fontFamily: "'Orbitron', monospace",
            fontWeight: '700'
          }
        });
      },

      error: (error) => {
        if (error.message === 'Account banned') {
          toast.error('🚫 Your account has been banned', {
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(127, 29, 29, 0.3))',
              color: '#dc2626',
              border: '1px solid #dc2626',
              fontFamily: "'Orbitron', monospace",
              fontWeight: '700'
            }
          });
          logout();
        } else if (error.message !== 'Service temporarily unavailable') {
          toast.error(error.message || 'An error occurred', {
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
              color: '#ef4444',
              border: '1px solid #ef4444',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: '700'
            }
          });
        }
      },

      maintenanceMode: (data) => {
        toast.error('🔧 Server is entering maintenance mode. You will be disconnected shortly.', { 
          duration: 10000,
          style: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(245, 158, 11, 0.2))',
            color: '#f59e0b',
            border: '1px solid #f59e0b',
            fontFamily: "'Orbitron', monospace",
            fontWeight: '700'
          }
        });
      },

      // Enhanced online users management with better validation
      onlineUsers: (users) => {
        if (!Array.isArray(users)) {
          return;
        }

        const currentTime = Date.now();
        
        // Avoid too frequent updates
        if (currentTime - onlineUsersLastUpdate < 500) {
          return;
        }

        // Enhanced validation and filtering
        const validUsers = users.filter(u => {
          if (!u || typeof u !== 'object') {
            return false;
          }
          
          if (!u._id || typeof u._id !== 'string') {
            return false;
          }

          if (!u.username && !u.inGameName) {
            return false;
          }

          if (u.banned === true) {
            return false;
          }

          const currentUserId = (user?.id || user?._id || '').toString();
          if (u._id.toString() === currentUserId) {
            return false;
          }

          return true;
        });

        // Sort users for consistent ordering
        const sortedUsers = validUsers.sort((a, b) => {
          const roleOrder = { 'main_admin': 0, 'supervisor': 1, 'approver': 2, 'player': 3 };
          const aRole = roleOrder[a.role] || 3;
          const bRole = roleOrder[b.role] || 3;
          
          if (aRole !== bRole) return aRole - bRole;
          
          const aName = a.inGameName || a.username || '';
          const bName = b.inGameName || b.username || '';
          return aName.localeCompare(bName);
        });

        setOnlineUsers(sortedUsers);
        setOnlineUsersLastUpdate(currentTime);
      },

      userOnline: (userData) => {
        if (!userData || !userData.userId) {
          return;
        }

        const currentUserId = (user?.id || user?._id || '').toString();
        const newUserId = userData.userId.toString();
        
        // Don't add current user to the list
        if (newUserId === currentUserId) {
          return;
        }

        // Don't add banned users
        if (userData.banned === true) {
          return;
        }

        setOnlineUsers(prev => {
          // Remove user if already exists to avoid duplicates
          const filtered = prev.filter(u => u._id?.toString() !== newUserId);
          
          // Add the user with updated online status
          const newUser = {
            _id: userData.userId,
            username: userData.username,
            inGameName: userData.inGameName,
            points: userData.points,
            role: userData.role,
            banned: userData.banned,
            isAdmin: userData.isAdmin,
            canApprove: userData.canApprove,
            subscriptionTier: userData.subscriptionTier
          };

          const updated = [...filtered, newUser].sort((a, b) => {
            const roleOrder = { 'main_admin': 0, 'supervisor': 1, 'approver': 2, 'player': 3 };
            const aRole = roleOrder[a.role] || 3;
            const bRole = roleOrder[b.role] || 3;
            
            if (aRole !== bRole) return aRole - bRole;
            
            const aName = a.inGameName || a.username || '';
            const bName = b.inGameName || b.username || '';
            return aName.localeCompare(bName);
          });

          return updated;
        });
      },

      userOffline: (data) => {
        if (!data || !data.userId) {
          return;
        }

        setOnlineUsers(prev => {
          const filtered = prev.filter(u => u._id?.toString() !== data.userId.toString());
          return filtered;
        });
      }
    };

    Object.entries(socketEvents).forEach(([event, handler]) => newSocket.on(event, handler));
    setSocket(newSocket);
  };

  const refreshTokenIfNeeded = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const timeUntilExpiry = decoded.exp * 1000 - Date.now();
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        const response = await axios.post('/api/auth/verify');
        if (!response.data.valid) logout();
      } else if (timeUntilExpiry <= 0) logout();
    } catch (error) {
      if (error.response?.status === 401) logout();
    }
  };

  const isTokenExpired = (token) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.exp * 1000 < Date.now();
    } catch { return true; }
  };

  // Enhanced login with COD-style notifications
  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/auth/login', credentials);
      
      if (response.data.banned) {
        return {
          success: false,
          banned: true,
          banData: {
            banReason: response.data.banReason,
            bannedBy: response.data.bannedBy,
            bannedAt: response.data.bannedAt
          }
        };
      }

      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      
      // Clear any existing online users data
      setOnlineUsers([]);
      setOnlineUsersLastUpdate(0);
      
      setTimeout(() => initializeSocket(token), 100);
      toast.success('🎮 ' + getTranslation('loginSuccessful', localStorage.getItem('preferred-language') || 'en', 'Login successful!') + ' ' + getTranslation('welcomeBattlefield', localStorage.getItem('preferred-language') || 'en', 'Welcome to the battlefield!'), {
        duration: 2000,
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
          color: '#10b981',
          border: '1px solid #10b981',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '700'
        }
      });
      return { success: true };
    } catch (error) {
      if (error.response?.status === 503) return { success: false, retry: true };
      const message = error.response?.data?.message || 'Login failed';
      toast.error(`❌ ${message}`, {
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
          color: '#ef4444',
          border: '1px solid #ef4444',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '700'
        }
      });
      if (error.response?.data?.banned) {
        return {
          success: false,
          banned: true,
          banData: {
            banReason: error.response.data.banReason,
            bannedBy: error.response.data.bannedBy,
            bannedAt: error.response.data.bannedAt
          }
        };
      }
      return { success: false };
    }
  };

  // Enhanced register with COD-style notifications
  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token, user: newUser } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);
      
      // Clear any existing online users data
      setOnlineUsers([]);
      setOnlineUsersLastUpdate(0);
      
      setTimeout(() => initializeSocket(token), 100);
      toast.success('🎯 Registration successful! Ready for combat!', {
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
          color: '#10b981',
          border: '1px solid #10b981',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '700'
        }
      });
      return { success: true };
    } catch (error) {
      if (error.response?.status === 503) return { success: false, retry: true };
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(`❌ ${message}`, {
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
          color: '#ef4444',
          border: '1px solid #ef4444',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '700'
        }
      });
      return { success: false };
    }
  };

  // Enhanced logout with COD-style notifications
  const logout = useCallback(async () => {
    try {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      
      // Clear all socket and online user states
      setConnectionStatus('disconnected');
      setSocketReady(false);
      setOnlineUsers([]);
      setOnlineUsersLastUpdate(0);
      
      const token = localStorage.getItem('token');
      if (token && serverHealth !== 'unhealthy') {
        try { await axios.post('/api/auth/logout'); } catch {}
      }
    } catch {} finally {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setReconnectAttempts(0);
      toast.dismiss();
      toast.success('👋 Logged out successfully. See you on the battlefield!', {
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(251, 191, 36, 0.2))',
          color: '#fbbf24',
          border: '1px solid #fbbf24',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '700'
        }
      });
    }
  }, [user, socket, serverHealth]);

  // Enhanced user update function with better state management
  const updateUser = useCallback((updatedUser = null) => {
    if (updatedUser) {
      setUser(updatedUser);
    } else {
      // Refresh user data from server
      axios.post('/api/auth/verify')
        .then(response => {
          if (response.data.valid) {
            setUser(response.data.user);
          }
        })
        .catch(console.error);
    }
  }, []);

  // Enhanced language change handler
  const handleLanguageChange = useCallback((language) => {
    setCurrentLanguage(language);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && socket && user) {
        // Force refresh connection when tab becomes visible
        if (!socket.connected) {
          socket.connect();
        } else {
          // Re-emit presence to refresh online status
          socket.emit("requestOnlineUsers");
        }
      }
    };
    
    const handleBeforeUnload = () => {
      if (socket) {
        socket.disconnect();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [socket, user]);

  if (loading) return <LoadingSpinner />;

  const getConnectionStatusColor = () => ({
    connected: 'connected',
    connecting: 'connecting',
    error: 'error',
    failed: 'failed'
  }[connectionStatus] || 'disconnected');

  const getConnectionStatusText = () => ({
    connected: 'Connected',
    connecting: reconnectAttempts > 0 ? `Reconnecting (${reconnectAttempts})` : 'Connecting',
    error: 'Connection Error',
    failed: 'Connection Failed'
  }[connectionStatus] || 'Disconnected');

  return (
    <div className="paypal-provider-wrapper">
      <PayPalScriptProvider options={paypalOptions}>
        <div className="router-wrapper">
          <Router>
            <div className="App">
              {/* RTL Debugger - only shows in development */}
              <RTLDebugger />
              
              <Routes>
                {/* Main tournament system route */}
                <Route 
                  path="/" 
                  element={
                    <CODTournamentSystem 
                      user={user}
                      socket={socket}
                      onlineUsers={onlineUsers}
                      onLogin={login}
                      onRegister={register}
                      onLogout={logout}
                      onUserUpdate={updateUser}
                      connectionStatus={connectionStatus}
                      socketReady={socketReady}
                      currentLanguage={currentLanguage}
                      t={t}
                      onLanguageChange={handleLanguageChange}
                    />
                  } 
                />
                
                {/* Public Profile Routes - Updated structure */}
                <Route
                  path="/profile/:identifier"
                  element={
                    <PublicProfile
                      user={user}
                      currentLanguage={currentLanguage}
                      t={t}
                      onLanguageChange={handleLanguageChange}
                    />
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PublicProfile
                      user={user}
                      currentLanguage={currentLanguage}
                      t={t}
                      onLanguageChange={handleLanguageChange}
                    />
                  }
                />
                <Route
                  path="/player/:identifier"
                  element={
                    <PublicProfile
                      user={user}
                      currentLanguage={currentLanguage}
                      t={t}
                      onLanguageChange={handleLanguageChange}
                    />
                  }
                />

                {/* Public Leaderboard route - Updated */}
                <Route
                  path="/leaderboard"
                  element={
                    <PublicLeaderboard
                      user={user}
                      currentLanguage={currentLanguage}
                      t={t}
                      onLanguageChange={handleLanguageChange}
                    />
                  }
                />
                
                {/* Forgot Password route */}
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Reset Password route */}
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Enhanced Toaster with COD Mobile styling */}
              <Toaster
                position="top-center"
                containerClassName="toaster-container"
                reverseOrder={false}
                gutter={8}
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
                    color: '#fff',
                    border: '1px solid rgba(234, 88, 12, 0.4)',
                    borderRadius: '8px',
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: '600',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                    clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)'
                  },
                  success: {
                    className: 'toast-success',
                    duration: 3000,
                    style: {
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
                      color: '#10b981',
                      border: '1px solid #10b981',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: '700'
                    },
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#0f172a',
                    },
                  },
                  error: {
                    className: 'toast-error',
                    duration: 5000,
                    style: {
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(239, 68, 68, 0.2))',
                      color: '#ef4444',
                      border: '1px solid #ef4444',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: '700'
                    },
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#0f172a',
                    },
                  },
                  loading: { 
                    className: 'toast-loading',
                    style: {
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(59, 130, 246, 0.2))',
                      color: '#3b82f6',
                      border: '1px solid #3b82f6',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: '700'
                    }
                  }
                }}
              />
              
              {/* Enhanced Socket status indicator for development */}
              {process.env.NODE_ENV === 'development' && (
                <div className={`socket-status-indicator ${getConnectionStatusColor()}`}>
                  <div className={`connection-dot ${getConnectionStatusColor()}`}></div>
                  <span>{getConnectionStatusText()}</span>
                  <div className="socket-debug-info">
                    <small>
                      👥 Users: {onlineUsers.length} | 
                      ⚡ Ready: {socketReady ? 'Yes' : 'No'} | 
                      💗 Health: {serverHealth} |
                      🌐 Lang: {currentLanguage} |
                      🔄 RTL: {isRTL(currentLanguage) ? 'Yes' : 'No'}
                    </small>
                  </div>
                </div>
              )}
              
            </div>
          </Router>
        </div>
      </PayPalScriptProvider>
    </div>
  );
}

export default App;